import axios, { AxiosInstance, AxiosResponse, AxiosError } from 'axios';
import { config } from '../config';
import authService from './auth';
import { ApiResponse, ApiError } from '../types';
import { AdventureInvite } from 'coop-adventure-engine';

class AdventureInvitesService {
  private axiosInstance: AxiosInstance;

  constructor() {
    this.axiosInstance = axios.create({
      baseURL: config.apiBaseUrl,
      timeout: 10000,
      headers: {
        'Content-Type': 'application/json',
      },
    });

    this.setupInterceptors();
  }

  private setupInterceptors(): void {
    // Request interceptor to add auth token
    this.axiosInstance.interceptors.request.use(
      async (config) => {
        const hasValidToken = await authService.ensureValidToken();
        if (hasValidToken) {
          const token = authService.getCurrentToken();
          if (token) {
            config.headers.Authorization = `Bearer ${token}`;
          }
        }
        return config;
      },
      (error) => Promise.reject(error)
    );

    // Response interceptor for error handling
    this.axiosInstance.interceptors.response.use(
      (response: AxiosResponse<ApiResponse>) => {
        return response;
      },
      (error: AxiosError<ApiResponse>) => {
        if (error.response?.status === 401) {
          // Handle unauthorized - auth service will handle token refresh
        }
        return Promise.reject(this.transformError(error));
      }
    );
  }

  private transformError(error: AxiosError<ApiResponse>): ApiError {
    if (error.response?.data?.error) {
      return error.response.data.error;
    }
    return {
      code: error.response?.status?.toString() || 'UNKNOWN',
      message: error.response?.data?.message || error.message || 'An error occurred',
      details: error.response?.data,
    };
  }

  /**
   * Send an adventure invite to a friend
   */
  async sendAdventureInvite(friendId: string, sessionId: string, playerSlot: string): Promise<AdventureInvite> {
    const response = await this.axiosInstance.post<ApiResponse<AdventureInvite>>('/api/adventure-invites', {
      friendId,
      sessionId,
      playerSlot,
    });
    if (!response.data.data) {
      throw new Error('Failed to send adventure invite');
    }
    // Convert date strings to Date objects
    const invite = response.data.data;
    return {
      ...invite,
      createdAt: new Date(invite.createdAt),
      ...(invite.respondedAt && { respondedAt: new Date(invite.respondedAt) }),
      ...(invite.expiresAt && { expiresAt: new Date(invite.expiresAt) }),
    } as AdventureInvite;
  }

  /**
   * Get pending adventure invites for the authenticated user
   */
  async getAdventureInvites(): Promise<AdventureInvite[]> {
    const response = await this.axiosInstance.get<ApiResponse<AdventureInvite[]>>('/api/adventure-invites');
    if (!response.data.data || !Array.isArray(response.data.data)) {
      return [];
    }
    // Convert date strings to Date objects
    return response.data.data.map((invite) => ({
      ...invite,
      createdAt: new Date(invite.createdAt),
      ...(invite.respondedAt && { respondedAt: new Date(invite.respondedAt) }),
      ...(invite.expiresAt && { expiresAt: new Date(invite.expiresAt) }),
    })) as AdventureInvite[];
  }

  /**
   * Get sent adventure invites by the authenticated user
   */
  async getSentAdventureInvites(): Promise<AdventureInvite[]> {
    const response = await this.axiosInstance.get<ApiResponse<AdventureInvite[]>>('/api/adventure-invites/sent');
    if (!response.data.data || !Array.isArray(response.data.data)) {
      return [];
    }
    // Convert date strings to Date objects
    return response.data.data.map((invite) => ({
      ...invite,
      createdAt: new Date(invite.createdAt),
      ...(invite.respondedAt && { respondedAt: new Date(invite.respondedAt) }),
      ...(invite.expiresAt && { expiresAt: new Date(invite.expiresAt) }),
    })) as AdventureInvite[];
  }

  /**
   * Respond to an adventure invite (accept or reject)
   */
  async respondToAdventureInvite(inviteId: string, accepted: boolean): Promise<{ success: boolean; error?: string }> {
    try {
      await this.axiosInstance.post<ApiResponse>(`/api/adventure-invites/${inviteId}/respond`, {
        accepted,
      });
      return { success: true };
    } catch (error) {
      const apiError = error as AxiosError<ApiResponse>;
      return {
        success: false,
        error: apiError.response?.data?.error?.message || 'Failed to respond to adventure invite',
      };
    }
  }

  /**
   * Cancel a sent adventure invite
   */
  async cancelAdventureInvite(inviteId: string): Promise<{ success: boolean; error?: string }> {
    try {
      await this.axiosInstance.delete<ApiResponse>(`/api/adventure-invites/${inviteId}`);
      return { success: true };
    } catch (error) {
      const apiError = error as AxiosError<ApiResponse>;
      return {
        success: false,
        error: apiError.response?.data?.error?.message || 'Failed to cancel adventure invite',
      };
    }
  }

  /**
   * Join an adventure from an invite
   * Gets the invite details and joins the session with the specified playerSlot
   */
  async joinAdventureFromInvite(inviteId: string): Promise<{ sessionId: string; playerId: string; player: any }> {
    // Get the invite to get sessionId and playerSlot
    const invites = await this.getAdventureInvites();
    
    const invite = invites.find(inv => inv.id === inviteId);
    
    if (!invite) {
      throw new Error('Adventure invite not found');
    }

    // Import apiService to join the session
    const apiService = (await import('./api')).default;
    
    // Join the session with the playerSlot from the invite
    const player = await apiService.joinSession(invite.sessionId, invite.playerSlot);
    
    return {
      sessionId: invite.sessionId,
      playerId: player.id,
      player: player
    };
  }

  /**
   * Select an adventure invite and navigate to target scene
   */
  async selectAdventureInvite(
    sessionId: string,
    playerId: string,
    inviteId: string,
    targetScene: string
  ): Promise<void> {
    await this.axiosInstance.post<ApiResponse>('/api/adventure-invites/select-invite', {
      inviteId,
      targetScene
    }, {
      params: {
        sessionId,
        playerId
      }
    });
  }
}

// Export singleton instance
const adventureInvitesService = new AdventureInvitesService();
export default adventureInvitesService;

